import React, { useMemo, useState } from "react";

const ICON_PATHS = {
    flame: "M12 2c.6 3.2-1.2 5-2.7 6.6C7.8 10.2 6 12 6 15a6 6 0 0 0 12 0c0-2.3-1-4-2.2-5.3-.3 1.5-1 2.6-2.3 3.2.5-3.9-.7-7.6-1.5-10.9z",
    bolt: "M13 2 4 14h7l-1 8 9-12h-7l1-8z",
    leaf: "M20 3c-9 0-15 4-15 11 0 1.6.4 3 1.1 4.2L4 20.3 5.4 21.7l2.1-2.1C8.7 20.5 10.2 21 12 21c6 0 9-6 8-18zm-9 13.5-1.4-1.4c2.6-2.6 5-4.3 7.4-5.6-2.1 1.8-4.2 4.2-6 7z",
};

const LevelIcon = ({ name, size }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d={ICON_PATHS[name]} />
    </svg>
);

const LEVELS = [
    { key: "high", label: "High", word: "High",   level: "HIGH",   icon: "flame", meaning: "Must have" },
    { key: "med",  label: "Med",  word: "Medium", level: "MEDIUM", icon: "bolt",  meaning: "Important" },
    { key: "low",  label: "Low",  word: "Low",    level: "LOW",    icon: "leaf",  meaning: "Nice to have" },
];

const presetKey = (p) => (p === "medium" ? "med" : p);

/**
 * Post-onboarding priority card (inline "Talk to Me" chat) -- one row per
 * requirement from the backend's personal_agent/priority_picker.py.
 * `fixed` rows are must-haves (questions 1-5) and only show a tag; every other
 * row needs the user's own High/Med/Low before the search runs -- the system
 * never picks a priority (the backend re-shows the card if one is missing).
 *
 * When one level holds two or more distance ("metric") wants, the user taps them in the order that should decide
 * first. Nothing is pre-filled: row order is never used, and Confirm waits until every such level is ordered
 * (skipped orders left those wants out of ranking with only a hint line, 2026-10-18 E2E).
 *
 * A card shown for a want added after results also lists the distance wants prioritised earlier
 * (`presetPriority`), read-only, so the new one can be ordered against them.
 */
export default function PriorityPicker({ items, disabled, onConfirm }) {
    const [priorities, setPriorities] = useState({});
    const [metricOrder, setMetricOrder] = useState({});
    const [confirmed, setConfirmed] = useState(false);

    const choosable = useMemo(() => items.filter((item) => !item.fixed && !item.presetPriority), [items]);
    const presets = useMemo(
        () => Object.fromEntries(items.filter((i) => i.presetPriority).map((i) => [i.id, presetKey(i.presetPriority)])),
        [items]
    );
    const effective = { ...presets, ...priorities };
    const setCount = choosable.filter((item) => priorities[item.id]).length;
    const counts = LEVELS.map(({ key }) => Object.values(priorities).filter((v) => v === key).length);
    const locked = disabled || confirmed;
    const remaining = choosable.length - setCount;

    const choose = (id, level) => {
        if (locked) return;
        setPriorities((prev) => {
            const next = { ...prev };
            if (next[id] === level) delete next[id];
            else next[id] = level;
            return next;
        });
    };

    const confirm = () => {
        if (locked || remaining > 0 || unordered.length) return;
        setConfirmed(true);
        const orders = completeOrders();
        onConfirm?.(priorities, Object.keys(orders).length ? orders : null);
    };

    const progress = choosable.length ? Math.round((setCount / choosable.length) * 100) : 100;

    // Levels with two or more metric rows, at least one of them new on this card, need the user's own order.
    const orderGroups = LEVELS
        .map(({ key, label, level }) => ({
            label, level,
            rows: items.filter((item) => !item.fixed && item.kind === "metric" && effective[item.id] === key),
        }))
        .filter((g) => g.rows.length > 1 && g.rows.some((r) => !r.presetPriority));

    const tapOrder = (level, ids, id) => {
        if (locked) return;
        setMetricOrder((prev) => {
            const current = (prev[level] || []).filter((x) => ids.includes(x));
            const at = current.indexOf(id);
            // Tapping a numbered row un-numbers it and everything after it.
            const next = at >= 0 ? current.slice(0, at) : [...current, id];
            return { ...prev, [level]: next };
        });
    };

    const completeOrders = () => Object.fromEntries(
        orderGroups
            .map((g) => [g.level, (metricOrder[g.level] || []).filter((x) => g.rows.some((r) => r.id === x))])
            .filter(([level, ids]) => ids.length === orderGroups.find((g) => g.level === level).rows.length)
    );
    const unordered = orderGroups.filter((g) => !(g.level in completeOrders()));

    return (
        <div className="priority-picker">
            <div className="pp-card">
                <div className="pp-head">
                    <div className="pp-head-top">
                        <div className="pp-title">Your requirements</div>
                        <div className="pp-progress" aria-label={`${setCount} of ${choosable.length} set`}>
                            {setCount} of {choosable.length}
                            <span className="pp-bar"><i style={{ width: `${progress}%` }} /></span>
                        </div>
                    </div>
                    <div className="pp-grid-row pp-cols" aria-hidden="true">
                        <span />
                        {LEVELS.map(({ key, label }) => <span key={key}>{label}</span>)}
                    </div>
                </div>

                {items.map((item) => {
                    const current = priorities[item.id];
                    const readOnly = item.fixed || !!item.presetPriority;
                    return (
                        <div
                            key={item.id}
                            className={`pp-row pp-grid-row${current ? " pp-row-set" : ""}`}
                            role={readOnly ? undefined : "radiogroup"}
                            aria-label={readOnly ? undefined : `${item.category}: ${item.text}`}
                        >
                            <div className="pp-req">
                                <div className="pp-label">{item.category}</div>
                                <div className="pp-value">{item.text}</div>
                                {item.id.startsWith("req-") && item.evaluability === "checked" && (
                                    <div className="pp-checked" title="Search checks every listing for this">✓ Checked</div>
                                )}
                                {item.id.startsWith("req-") && item.evaluability === "not_validated" && (
                                    <div className="pp-unverified" title="We can measure this, but our map data for it hasn't been validated yet, so it won't change your results">
                                        Map data not validated yet
                                    </div>
                                )}
                                {item.verifiable === false && (
                                    <div className="pp-unverified" title="We note this, but can't check listings for it yet, so it won't change your results">
                                        Can't verify yet
                                    </div>
                                )}
                            </div>
                            {item.fixed && <span className="pp-fixed-tag">Fixed</span>}
                            {!item.fixed && item.presetPriority && (
                                <span className="pp-fixed-tag">
                                    {LEVELS.find((l) => l.key === presets[item.id])?.label || item.presetPriority} · set earlier
                                </span>
                            )}
                            {!readOnly && LEVELS.map(({ key, label }) => (
                                    <button
                                        key={key}
                                        type="button"
                                        className={`pp-btn pp-btn-${key}${current === key ? " on" : ""}`}
                                        role="radio"
                                        aria-checked={current === key}
                                        aria-label={`${label} priority`}
                                        disabled={locked}
                                        onClick={() => choose(item.id, key)}
                                    >
                                        <LevelIcon name={LEVELS.find((l) => l.key === key).icon} size={18} />
                                    </button>
                                ))}
                        </div>
                    );
                })}

                {orderGroups.map((g) => {
                    const ids = g.rows.map((r) => r.id);
                    const current = (metricOrder[g.level] || []).filter((x) => ids.includes(x));
                    return (
                        <div key={g.level} className="pp-order" role="group" aria-label={`Order of ${g.label} distance wants`}>
                            <div className="pp-order-title">{g.label}: which should decide first?</div>
                            <div className="pp-order-hint">Tap them in order, the one that matters most first.</div>
                            {g.rows.map((row) => {
                                const n = current.indexOf(row.id);
                                return (
                                    <button
                                        key={row.id}
                                        type="button"
                                        className={`pp-order-row${n >= 0 ? " on" : ""}`}
                                        disabled={locked}
                                        aria-pressed={n >= 0}
                                        onClick={() => tapOrder(g.level, ids, row.id)}
                                    >
                                        <span className="pp-order-num">{n >= 0 ? n + 1 : "–"}</span>
                                        {row.text}
                                    </button>
                                );
                            })}
                        </div>
                    );
                })}

                <div className="pp-foot">
                    <div className="pp-counts">
                        {LEVELS.map(({ key, word }, i) => (
                            <span key={key} className={`pp-c-${key}`}><b>{counts[i]}</b> {word}</span>
                        ))}
                    </div>
                    <button type="button" className="pp-confirm" disabled={locked || remaining > 0 || unordered.length > 0} onClick={confirm}>
                        {confirmed ? "Confirmed ✓" : "Confirm priorities"}
                    </button>
                </div>
                {!locked && remaining > 0 && (
                    <div className="pp-hint" role="status">
                        Choose High, Med or Low for {remaining === 1 ? "the last item" : `the ${remaining} remaining items`}
                    </div>
                )}
                {!locked && remaining === 0 && unordered.length > 0 && (
                    <div className="pp-hint" role="status">
                        Tap your {unordered.map((g) => g.label).join(" and ")} distance wants in order to continue
                    </div>
                )}
            </div>
            <div className="pp-legend" aria-hidden="true">
                {LEVELS.map(({ key, icon, meaning }) => (
                    <span key={key} className={`pp-legend-${key}`}><LevelIcon name={icon} size={14} /> {meaning}</span>
                ))}
            </div>
        </div>
    );
}
